import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import IndexSidebar from 'flarum/forum/components/IndexSidebar';
import UserControls from 'flarum/forum/utils/UserControls';
import LinkButton from 'flarum/common/components/LinkButton';
import Button from 'flarum/common/components/Button';
import { installSupportNotificationGrouping } from './components/notifications';
import { tr } from './utils/translate';
import { installRealtime } from './utils/live';
import { basePath, BASE_PATH, showNavInSidebar, showNavInAccountMenu, readForumAttribute, showError } from './utils/helpers';
import { clearTargetOnModalClose } from './utils/guestAction';
import { ticketTypes } from '../common/ticketTypes';
import addSupportSearch from './addSupportSearch';
export { default as extend } from './extend';
app.initializers.add('linkrobins-support', () => {
  app.support = {
    ticketTypes
  };
  addSupportSearch();

  // A guest sent to the log-in modal by a support action carries a target
  // through the post-login reload. Closing the modal without logging in
  // drops it, so it can never redirect anyone later.
  clearTargetOnModalClose();

  // Group support notifications under a translatable "Support" heading in the
  // notifications dropdown, instead of the generic forum-title group.
  installSupportNotificationGrouping();

  // Live replies and ticket updates, when flarum/realtime is enabled.
  installRealtime();

  // NotificationGrid lives in a lazily-loaded chunk, so it isn't in the
  // registry at init time -- a direct import resolves to undefined. Use the
  // string-path form of extend(), which defers resolution until the module
  // actually loads.
  extend('flarum/forum/components/NotificationGrid', 'notificationTypes', items => {
    items.add('linkrobinsSupportNewReply', {
      name: 'linkrobinsSupportNewReply',
      icon: 'fas fa-life-ring',
      label: tr('settings.notify_new_reply_label', 'Someone replies to your support ticket')
    });
    items.add('linkrobinsSupportNewTicket', {
      name: 'linkrobinsSupportNewTicket',
      icon: 'fas fa-ticket-alt',
      label: tr('settings.notify_new_ticket_label', 'A new support ticket is opened')
    });
    items.add('linkrobinsSupportTicketStatusChanged', {
      name: 'linkrobinsSupportTicketStatusChanged',
      icon: 'fas fa-exchange-alt',
      label: tr('settings.notify_status_changed_label', "A support ticket's status changes")
    });
    items.add('linkrobinsSupportTicketAssigned', {
      name: 'linkrobinsSupportTicketAssigned',
      icon: 'fas fa-user-check',
      label: tr('settings.notify_assigned_label', 'A support ticket is assigned to you')
    });
    items.add('linkrobinsSupportAwaitingReminder', {
      name: 'linkrobinsSupportAwaitingReminder',
      icon: 'fas fa-hourglass-half',
      label: tr('settings.notify_awaiting_reminder_label', 'Support has been waiting a few days for my reply')
    });
  });

  // Appeal-ban toggle in the user's moderation-controls dropdown (the same menu
  // as Suspend). Shown only to users with the manage_appeal_bans permission.
  // UserControls is a plain object (not a component class), so we extend the
  // object itself -- extending `.prototype` would silently no-op.
  extend(UserControls, 'moderationControls', (items, user) => {
    if (!readForumAttribute('canManageSupportAppealBans')) return;
    if (!user || typeof user.attribute !== 'function') return;
    const banned = !!user.attribute('supportAppealBanned');
    items.add('linkrobinsSupportAppealBan', m(Button, {
      icon: banned ? 'fas fa-unlock' : 'fas fa-ban',
      onclick: () => {
        user.save({
          supportAppealBanned: !banned
        }).then(() => {
          m.redraw();
        }).catch(() => {
          showError(tr('user_controls.toggle_failed', 'Could not update the appeal-ban status.'));
        });
      }
    }, banned ? tr('user_controls.allow_appeals', 'Allow support appeals') : tr('user_controls.disallow_appeals', 'Disallow support appeals')));
  });
  const supportLink = () => m(LinkButton, {
    href: basePath() + BASE_PATH,
    icon: 'fas fa-life-ring'
  }, tr('nav', 'Support'));

  // Support link in the forum's sidebar nav. Priority -11 slots it directly
  // below flarum/tags' "Tags" link (-10) and above its separator (-12) and tag
  // list (-14), so it doesn't sit oddly between "All Discussions" and the tags
  // block. Without the tags extension it simply lands under the remaining nav
  // links.
  extend(IndexSidebar.prototype, 'navItems', items => {
    if (!app.session || !app.session.user || !showNavInSidebar()) return;
    items.add('linkrobins-support', supportLink(), -11);
  });

  // And in the account menu, under Settings (50) and above Administration (0),
  // with the other entries that are about you rather than about the forum.
  // String-path extend for the same reason as NotificationGrid above: it
  // defers resolution until the module is actually loaded.
  extend('flarum/forum/components/SessionDropdown', 'items', items => {
    if (!app.session || !app.session.user || !showNavInAccountMenu()) return;
    items.add('linkrobins-support', supportLink(), 40);
  });

  // Support stats for staff, in the account menu beside the Support link,
  // opening a modal the way LR Birdseye's Analytics item does.
  extend('flarum/forum/components/SessionDropdown', 'items', items => {
    if (!app.session || !app.session.user || !readForumAttribute('canHandleSupportTickets')) return;
    items.add('linkrobins-support-stats', m(Button, {
      icon: 'fas fa-chart-bar',
      onclick: () => app.modal.show(() => import(/* webpackChunkName: 'forum/components/SupportStatsModal', webpackMode: 'lazy-once' */ './components/SupportStatsModal'))
    }, tr('stats.title', 'Support stats')), 39);
  });
});flarum.reg.addChunkModule('993', '707', 'linkrobins-support', 'forum/components/SupportStatsModal');